import type { CostBucket } from '@orgus/protocol';
import type { ReactNode } from 'react';
import { OTHER_KEY, StackedBarChart, formatMoney, formatMoneyCompact } from '@orgus/ui';
import type { StackDatum } from '@orgus/ui';
import { dayLabel } from './model.ts';

/** The smallest "nice" number (1, 2, 2.5, 5 times a power of ten) at or above v. */
export function niceCeil(v: number): number {
  if (!(v > 0)) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= v) return m * p;
  return 10 * p;
}

const MONTH = 'month';

/** One sentence the chart proves, for screen readers. */
export function dailySummary(days: CostBucket[]): string {
  if (!days.length) return 'No spend in the last 30 days';
  const top = days.reduce((a, b) => (b.cost > a.cost ? b : a));
  const total = days.reduce((n, d) => n + d.cost, 0);
  return `${formatMoney(total)} over ${days.length} days; the most was ${formatMoney(top.cost)} on ${dayLabel(top.key)}`;
}

/**
 * Daily spend for the last 30 days (visx, data-viz.md: spend per day is bars from zero). Days of
 * this month are the first data colour; earlier days are "Other" grey. A daily budget is a
 * dashed threshold line with its label. Hover or focus a bar to read it; the Table toggle has
 * every number.
 */
export function DailySpendChart({
  days,
  monthStart,
  dailyLimit,
  actions,
  partial,
  className,
}: {
  days: CostBucket[];
  monthStart: number;
  dailyLimit?: number | null;
  actions?: ReactNode;
  partial?: ReactNode;
  className?: string;
}) {
  const d = new Date(monthStart);
  const monthKeyStart = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  const data: StackDatum[] = days.map((b) => ({ label: dayLabel(b.key), values: { [b.key >= monthKeyStart ? MONTH : OTHER_KEY]: b.cost } }));
  const hasEarlier = days.some((b) => b.key < monthKeyStart);
  return (
    <StackedBarChart
      className={className}
      title="Spend per day, last 30 days"
      // A section of the Spend page, a peer of Budgets and Top sessions (h2), right under the h1.
      headingLevel={2}
      summary={dailySummary(days)}
      keys={[{ key: MONTH, label: 'This month' }, ...(hasEarlier ? [{ key: OTHER_KEY, label: 'Earlier' }] : [])]}
      order={[MONTH]}
      data={data}
      format={formatMoney}
      axisFormat={formatMoneyCompact}
      threshold={dailyLimit ? { value: dailyLimit, label: `Daily budget ${formatMoneyCompact(dailyLimit)}` } : undefined}
      height={220}
      actions={actions}
      partial={partial}
      emptyText={{ title: 'No spend yet', description: 'As soon as a Claude Code session on a connected machine uses tokens, its cost shows here by day.' }}
    />
  );
}
